import { CheckCircle, User, MapPin } from "@phosphor-icons/react";
import { fmtTime12 } from "@/lib/scheduleWindows";
import { KIND_STYLE, KIND_BAR, STAGES } from "@/lib/calendarConstants";

export function CalendarEventChip({ e, onDragStart, onDragEnd, onClick }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`cal-event-${e.entity}-${e.id}-${e.kind}`}
      className={`group flex cursor-grab overflow-hidden rounded-md border bg-zinc-950 text-left transition-all duration-150 hover:-translate-y-px active:cursor-grabbing ${
        e.overdue ? "border-red-500/50 ring-1 ring-red-500/40" : "border-zinc-800 hover:border-zinc-700"
      }${e.archived ? " opacity-60 saturate-50" : ""}`}
    >
      <span className={`w-1 shrink-0 ${KIND_BAR[e.kind] || "bg-zinc-600"}`} />
      <span className="min-w-0 flex-1 px-2 py-1.5">
        <span className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1">
            <span
              className={`cal-kind-chip inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                KIND_STYLE[e.kind] || "border-zinc-700 bg-zinc-800 text-zinc-200"
              }`}
            >
              {e.kind}
            </span>
            {STAGES.includes(e.stage) && (
              <span
                data-testid={`cal-stage-${e.id}-${e.kind}`}
                className="inline-flex rounded-full border border-zinc-700 bg-zinc-800/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-zinc-400"
              >
                {e.stage}
              </span>
            )}
            {e.archived && (
              <span
                data-testid={`cal-archived-${e.id}-${e.kind}`}
                className="inline-flex rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-300"
              >
                Archived
              </span>
            )}
            {e.done && (
              <span
                data-testid={`cal-done-${e.id}-${e.kind}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-300"
              >
                <CheckCircle size={9} weight="fill" /> Completed
              </span>
            )}
            {e.confirmed && (
              <CheckCircle
                size={13}
                weight="fill"
                className="text-emerald-500"
                data-testid={`cal-confirmed-${e.job_id}`}
              />
            )}
          </span>
          {(e.time || (e.window && e.window !== "All day")) && (
            <span className="font-mono-data text-[10px] text-zinc-500">
              {[e.time ? fmtTime12(e.time) : "", e.window && e.window !== "All day" ? e.window : ""].filter(Boolean).join(" · ")}
            </span>
          )}
          {e.route_eta && (
            <span
              className="font-mono-data text-[10px] font-bold text-indigo-300"
              data-testid={`cal-eta-${e.id}-${e.kind}`}
              title="Planned arrival from Route Planner"
            >
              ETA {new Date(e.route_eta).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
          )}
        </span>
        <span className="mt-1 block truncate text-xs font-semibold text-zinc-100">
          {e.title || e.code}
        </span>
        {e.areas?.length > 0 && (
          <span
            className="block truncate text-[10px] font-semibold uppercase tracking-widest text-indigo-300"
            data-testid={`cal-areas-${e.id}-${e.kind}`}
            title={e.areas.join(", ")}
          >
            {e.kind} · {e.areas.join(", ")}
          </span>
        )}
        {e.customer_name && (
          <span className="block truncate text-[11px] text-zinc-500">
            {e.customer_name}
          </span>
        )}
        {(e.crew || e.address) && (
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-zinc-500">
            {e.crew && (
              <span className="inline-flex items-center gap-1" data-testid={`cal-crew-${e.id}-${e.kind}`}>
                <User size={10} weight="bold" /> {e.crew}
              </span>
            )}
            {e.address && (
              <span className="inline-flex min-w-0 items-center gap-1" title={e.address}>
                <MapPin size={10} weight="bold" className="shrink-0" />
                <span className="truncate">{e.address}</span>
              </span>
            )}
          </span>
        )}
      </span>
    </div>
  );
}
